import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import GolfLoadingSpinner from '../components/GolfLoadingSpinner';
import './FittersCornerPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ---------------------------------------------------------------------------
// Pass 01 ("cut the bench") — ported from imp_mds/interim_work/prototype/
// bench2.template.html's renderCut()/pick()/setBand()/renderSide() cuts panel.
// Wired to real catalog + the X1 endpoints (GET/POST/DELETE /expert/rules)
// instead of the prototype's static JSON.
// ---------------------------------------------------------------------------

interface ShaftRow {
  id: number;
  brand: string;
  model: string;
  standard_flex: string | null;
  weight: number | null;
  torque: number | null;
  launch: string | null;
  material: string | null;
}

interface HeadRow {
  id: number;
  brand: string;
  model: string;
  loft: number | null;
  moi_combined: number | null;
  cg_distance: string | null;
}

interface FitRule {
  id: number;
  profile_id: number | null;
  category: string;
  target: 'shaft' | 'head';
  dimension: string;
  operator: string;
  value: any;
  action: string;
  reason: string | null;
  n_matched: number;
  n_matched_live: number | null;
  agrees_with_engine: boolean | null;
  band_low: number | null;
  band_high: number | null;
}

// ---------------------------------------------------------------------------
// Pass 02 ("prescribe the build") — X3. Ported from bench2.template.html's
// PX/renderPx()/setPx()/tglLevel()/dist(). Wired to GET/POST/DELETE
// /expert/prescriptions and GET /expert/prescriptions/dims. The continuous
// dimensions' dual-handle drag slider (IntervalSlider, below) is a port of
// the prototype's .rng-wrap, including the engine-ghost tick — see
// UI_DESIGN_PLAN.md §4.2/D2a.
// ---------------------------------------------------------------------------

interface PxSubMeta { dimension: string; name: string; ext: [number, number]; }
interface PxDimMeta {
  dimension: string;
  name: string;
  kind: 'continuous' | 'ordinal' | 'nominal';
  unit: string;
  provenance: string | null;
  warn: boolean;
  ext?: [number, number];
  ghost?: number;
  levels?: string[];
  counts?: Record<string, number>;
  subs?: PxSubMeta[];
  note?: string;
}

interface Prescription {
  id: number;
  profile_id: number;
  category: string;
  target: 'shaft' | 'head';
  dimension: string;
  kind: string;
  ideal: number | null;
  range_low: number | null;
  range_high: number | null;
  levels: string[] | null;
  primary_level: string | null;
  sub_specs: Record<string, Record<string, [number, number]>> | null;
  importance: number;
  no_view: boolean;
}

// ---------------------------------------------------------------------------
// Pass 03 ("score the strip") — X4a. Ported from bench2.template.html's
// renderScore()/arm()/place()/unplace()/stripClick(), lines ~801-881. Consumes
// X3's GET /expert/candidates (already-stratified 12-item set) and writes
// GET/POST/DELETE /expert/scores. `distance` is on every candidate but must
// never be rendered during placement (§0.6: "d is hidden during scoring and
// revealed after" — showing it turns the score into a restatement of the metric).
// ---------------------------------------------------------------------------

interface Candidate {
  item_id: number;
  target: 'shaft' | 'head';
  label: string;
  spec: string;
  distance: number;
  slice: 'ontarget' | 'near' | 'workable' | 'cut' | 'current';
  is_anchor: boolean;
}

interface ItemScore {
  id: number;
  profile_id: number;
  category: string;
  target: 'shaft' | 'head';
  item_id: number;
  score: number;
  slice: string;
  distance: number;
  is_anchor: boolean;
}

// ---------------------------------------------------------------------------
// Pass 04 ("build the shortlist") — X4b, §0.11. No prototype exists for this
// pass (§0.11: "not prototyped anywhere") — this is a real design pass, not a
// port. Two surfaces, sharing GET/POST/DELETE /expert/builds:
//
// R1 build matrix: rows are the heads scored in Pass 03, in his own score
// order (up to ~10). Under each head, up to 5 shafts from a palette — the
// shafts scored in Pass 03 — ranked and scored. The palette is a copy source:
// picking a shaft under one head leaves it available under every other head.
// R2 ideal-fit picker: up to 10 fixed slots, repeats allowed, each specifying
// a head (with its loft) and a shaft (with its flex), scored on a 0.1-step
// slider. Slot identity (`row_order`) is what the backend upserts on, not the
// head/shaft content — R2 explicitly wants the same head at two lofts to be
// two different rows.
// ---------------------------------------------------------------------------

interface BuildScore {
  id: number;
  profile_id: number;
  category: string;
  head_id: number | null;
  shaft_id: number | null;
  score: number;
  is_dream: boolean;
  note: string | null;
  rank_in_head: number | null;
  head_loft: number | null;
  shaft_flex: string | null;
  row_order: number | null;
  source: 'matrix' | 'ideal' | 'probe';
}

const IDEAL_SLOTS = Array.from({ length: 10 }, (_, i) => i + 1);

// ---------------------------------------------------------------------------
// Pass 05 ("reveal") — X5. Two signals off GET /expert/reveal: the score/slice
// summary (mean score per target/slice, off X4a's expert_item_scores -- §0.10:
// "slice means fell monotonically") and this golfer's fit rules with
// agrees_with_engine (computed at write time by POST /expert/rules since this
// session; see engine_agreement.py for which rules are checkable at all).
// ---------------------------------------------------------------------------

interface RevealSliceRow { slice: string; mean_score: number; n: number; }
interface RevealData {
  profile_id: number;
  category: string;
  slices: { shaft: RevealSliceRow[]; head: RevealSliceRow[] };
  rules: FitRule[];
}

const SLICE_LABEL: Record<string, string> = {
  ontarget: 'On target', near: 'Near', workable: 'Workable', cut: 'Cut', current: 'Current (anchor)',
};

// §0.4's 5-band strip, distinct from Pass 01's 3-band cut scale.
const SCORE_BANDS = [
  { i: 0, lo: 0, hi: 2, name: 'Unplayable' },
  { i: 1, lo: 3, hi: 4, name: 'Poor' },
  { i: 2, lo: 5, hi: 6, name: 'Workable' },
  { i: 3, lo: 7, hi: 8, name: 'Good' },
  { i: 4, lo: 9, hi: 10, name: 'Ideal' },
];
const scoreBandOf = (s: number) => (s <= 2 ? 0 : s <= 4 ? 1 : s <= 6 ? 2 : s <= 8 ? 3 : 4);
const scoreBandColor = (s: number) => `var(--sb${scoreBandOf(s)})`;

// severity bands used by Pass 01 — plan §0.4
const BANDS = [
  { i: 0, lo: 0, hi: 2, name: 'Unplayable' },
  { i: 1, lo: 3, hi: 4, name: 'Poor' },
  { i: 2, lo: 5, hi: 6, name: 'Compromise' },
];
const bandColor = (i: number) => ['var(--b0)', 'var(--b1)', 'var(--b2)'][i];

// dirty-vocab normalisation, mirrors backend/golf_api/app/core/fit_rule_vocabulary.py
const ORDINAL_ALIASES: Record<string, string> = {
  'ultra-low': 'Ultra-Low', low: 'Low', 'low-mid': 'Low-Mid', 'mid-low': 'Low-Mid',
  mid: 'Mid', 'mid-high': 'Mid-High', high: 'High', 'v. high': 'V. High',
};
const normLaunch = (v: string | null): string | null =>
  v == null ? null : (ORDINAL_ALIASES[v.trim().toLowerCase()] || v.trim());
const normCG = (v: string | null): string | null =>
  v == null ? null : v.replace(/\s*\([^)]*\)\s*$/, '').trim();

const LAUNCH_ORDER = ['Low', 'Low-Mid', 'Mid', 'Mid-High', 'High'];
const FLEX_ORDER = ['L', 'A', 'R', 'S', 'X', 'XX', 'TX'];

const WT_BANDS: [number, number, string][] = [[0, 55, '≤55 g'], [55, 65, '55–65'], [65, 75, '65–75'], [75, 120, '>75 g']];
const TQ_BANDS: [number, number, string][] = [[0, 3, '≤3.0'], [3, 4, '3.0–4.0'], [4, 5, '4.0–5.0'], [5, 9, '>5.0']];
const LOFT_BANDS: [number, number, string][] = [[7, 9, '≤9°'], [9, 10.5, '9–10.5°'], [10.5, 12, '10.5–12°'], [12, 15, '>12°']];
const MOI_BANDS: [number, number, string][] = [[0, 8200, '<8200'], [8200, 9000, '8200–9000'], [9000, 11000, '≥9000']];

// Generic rule matcher — used for both pool cutting and chip re-derivation.
// Keep this in lockstep with backend/golf_api/app/core/fit_rule_vocabulary.py's
// count_matches(); it is a display-time mirror, not the source of truth (POST
// recomputes n_matched server-side).
function matchesRule(target: 'shaft' | 'head', dimension: string, operator: string, value: any, item: ShaftRow | HeadRow): boolean {
  if (target === 'shaft') {
    const s = item as ShaftRow;
    switch (dimension) {
      case 'standard_flex': return s.standard_flex === value;
      case 'weight': return s.weight != null && s.weight > value[0] && s.weight <= value[1];
      case 'torque': return s.torque != null && s.torque > value[0] && s.torque <= value[1];
      case 'launch': return normLaunch(s.launch) === value;
      case 'material': return s.material === value;
      default: return false;
    }
  } else {
    const h = item as HeadRow;
    switch (dimension) {
      case 'loft': return h.loft != null && h.loft > value[0] && h.loft <= value[1];
      case 'moi_combined': return h.moi_combined != null && h.moi_combined >= value[0] && h.moi_combined < value[1];
      case 'cg_distance': return normCG(h.cg_distance) === value;
      case 'manufacturer': return h.brand === value;
      default: return false;
    }
  }
}

interface DimOption { key: string; label: string; operator: string; value: any; }
interface DimConfig { target: 'shaft' | 'head'; dimension: string; name: string; prov: string; warn?: boolean; opts: DimOption[]; }

function buildDims(shafts: ShaftRow[], heads: HeadRow[]): DimConfig[] {
  const distinctSorted = (vals: (string | null)[], order?: string[]) => {
    const set = Array.from(new Set(vals.filter((v): v is string => !!v)));
    if (order) {
      return set.sort((a, b) => {
        const ia = order.indexOf(a), ib = order.indexOf(b);
        if (ia === -1 && ib === -1) return a.localeCompare(b);
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      });
    }
    return set.sort();
  };

  const bandOpts = (bands: [number, number, string][]): DimOption[] =>
    bands.map(([lo, hi, label]) => ({ key: label, label, operator: 'between', value: [lo, hi] }));

  return [
    {
      target: 'shaft', dimension: 'standard_flex', name: 'Flex', prov: '100% populated',
      opts: distinctSorted(shafts.map(s => s.standard_flex), FLEX_ORDER)
        .map(f => ({ key: f, label: f, operator: 'eq', value: f })),
    },
    { target: 'shaft', dimension: 'weight', name: 'Weight', prov: '100%', opts: bandOpts(WT_BANDS) },
    { target: 'shaft', dimension: 'torque', name: 'Torque', prov: '~97%', opts: bandOpts(TQ_BANDS) },
    {
      target: 'shaft', dimension: 'launch', name: 'Launch', prov: '100% · normalised',
      opts: distinctSorted(shafts.map(s => normLaunch(s.launch)), LAUNCH_ORDER)
        .map(l => ({ key: l, label: l, operator: 'eq', value: l })),
    },
    {
      target: 'shaft', dimension: 'material', name: 'Material', prov: '100%',
      opts: distinctSorted(shafts.map(s => s.material)).map(m => ({ key: m, label: m, operator: 'eq', value: m })),
    },
    { target: 'head', dimension: 'loft', name: 'Loft', prov: '100%', opts: bandOpts(LOFT_BANDS) },
    { target: 'head', dimension: 'moi_combined', name: 'MOI', prov: '~63% estimated', warn: true, opts: bandOpts(MOI_BANDS) },
    {
      target: 'head', dimension: 'cg_distance', name: 'CG position', prov: '≈84% · normalised',
      opts: distinctSorted(heads.map(h => normCG(h.cg_distance))).map(c => ({ key: c, label: c, operator: 'eq', value: c })),
    },
    {
      target: 'head', dimension: 'manufacturer', name: 'Brand', prov: '100%',
      opts: distinctSorted(heads.map(h => h.brand)).map(b => ({ key: b, label: b, operator: 'eq', value: b })),
    },
  ];
}

const BENCH_PASSES = ['cut', 'prescribe', 'score', 'build', 'reveal'] as const;
type BenchPass = typeof BENCH_PASSES[number];

const FittersCornerPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  // X6: the dashboard's "resume in progress" card links here with a specific
  // profile + the pass this expert last worked on, so a resumed session opens
  // exactly what was left in progress instead of whatever /profiles/next hands
  // back next (which ignores bench-table progress entirely -- see
  // GET /expert/profiles/{profile_id}'s docstring).
  const [searchParams] = useSearchParams();
  const resumeProfileId = searchParams.get('profile_id');
  const resumePassParam = searchParams.get('pass');
  // Category comes from the URL (?category=iron) so the dashboard can send a
  // fitter into an iron pass. No visible switcher yet -- Sitting 3/4 backlog.
  const categoryParam = searchParams.get('category');
  const category = categoryParam === 'iron' || categoryParam === 'wedge' ? categoryParam : 'driver';

  // D3: "skip this golfer for now" is a client-side list, per browser, passed to
  // the bench queue as exclude_ids. Lost on a new machine -- acceptable for one
  // tester round, stated in the brief.
  const SKIP_KEY = 'bench_skip_ids';
  const [skipIds, setSkipIds] = useState<number[]>(() => {
    try { return JSON.parse(localStorage.getItem(SKIP_KEY) || '[]'); } catch { return []; }
  });
  const [queueEmpty, setQueueEmpty] = useState(false);

  const [profile, setProfile] = useState<any | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>(
    () => (localStorage.getItem('bench_theme') as 'light' | 'dark') || 'light'
  );
  useEffect(() => { localStorage.setItem('bench_theme', theme); }, [theme]);
  const [shafts, setShafts] = useState<ShaftRow[]>([]);
  const [heads, setHeads] = useState<HeadRow[]>([]);
  const [rules, setRules] = useState<FitRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [armed, setArmed] = useState<{ target: 'shaft' | 'head'; dimension: string; opt: DimOption } | null>(null);
  const [busy, setBusy] = useState(false);
  const [pass, setPass] = useState<BenchPass>(
    (BENCH_PASSES as readonly string[]).includes(resumePassParam || '') ? (resumePassParam as BenchPass) : 'cut'
  );
  const [pxDims, setPxDims] = useState<{ shaft: PxDimMeta[]; head: PxDimMeta[] }>({ shaft: [], head: [] });
  // Pass 02/03 (prescribe + score-strip retrieval) is not built for every category.
  // A gap there must not take down Pass 01/04, so the prescription calls load
  // separately and set this flag on failure instead of the fatal bench error.
  const [prescribeUnavailable, setPrescribeUnavailable] = useState(false);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [scoreTarget, setScoreTarget] = useState<'shaft' | 'head'>('shaft');
  const [candidates, setCandidates] = useState<{ shaft: Candidate[]; head: Candidate[] }>({ shaft: [], head: [] });
  const [candidatesLoaded, setCandidatesLoaded] = useState<{ shaft: boolean; head: boolean }>({ shaft: false, head: false });
  const [scores, setScores] = useState<ItemScore[]>([]);
  const [armedScore, setArmedScore] = useState<number | null>(null); // item_id armed on the tray

  // C3: the lane-packing collision test needs the strip's real pixel width, not
  // a hard-coded percentage, so it stays correct at any viewport (§4.1 C3).
  const stripRef = useRef<HTMLDivElement>(null);
  const [stripWidth, setStripWidth] = useState(0);
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) setStripWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [pass, scoreTarget]);

  const [buildScores, setBuildScores] = useState<BuildScore[]>([]);
  const [activeHeadId, setActiveHeadId] = useState<number | null>(null); // R1: which head row keyboard-add targets
  const [probeMode, setProbeMode] = useState(false); // R1: next combo placed is the seeded deliberate mismatch
  // R2: a slot only reaches the server once it has a head *and* a shaft, so a
  // half-picked row (head chosen, shaft not yet) needs somewhere to live that
  // isn't the committed buildScores list -- otherwise picking the shaft next
  // has nothing to merge the head selection with. Keyed by row_order.
  const [idealDraft, setIdealDraft] = useState<Record<number, { head_id: number | null; shaft_id: number | null }>>({});

  const [reveal, setReveal] = useState<RevealData | null>(null);
  const [revealLoading, setRevealLoading] = useState(false);

  const authHeaders = useCallback(() => {
    const storageType = localStorage.getItem('auth_storage_type');
    const storage = storageType === 'session' ? sessionStorage : localStorage;
    const token = storage.getItem('access_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setQueueEmpty(false);
    try {
      const headers = authHeaders();
      const nextUrl = `${API_BASE_URL}/api/v1/expert/profiles/next?queue=bench&category=${category}`
        + `&only_synthetic=true&exclude_ids=${skipIds.join(',')}`;
      const profileRes = resumeProfileId
        ? await axios.get(`${API_BASE_URL}/api/v1/expert/profiles/${resumeProfileId}`, { headers })
        : await axios.get(nextUrl, { headers });
      const profileId = profileRes.data.profile.id;

      const [catalogRes, rulesRes, scoresRes, buildsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/v1/expert/rules/catalog?category=${category}`, { headers }),
        axios.get(`${API_BASE_URL}/api/v1/expert/rules?profile_id=${profileId}&category=${category}`, { headers }),
        axios.get(`${API_BASE_URL}/api/v1/expert/scores?profile_id=${profileId}&category=${category}`, { headers }),
        axios.get(`${API_BASE_URL}/api/v1/expert/builds?profile_id=${profileId}&category=${category}`, { headers }),
      ]);

      setProfile(profileRes.data.profile);
      setShafts(catalogRes.data.shafts);
      setHeads(catalogRes.data.heads);
      setRules(rulesRes.data.rules);
      setScores(scoresRes.data.scores);
      setBuildScores(buildsRes.data.builds);

      // Pass 02/03 load separately -- a category without prescribe/score built
      // (or a transient failure there) leaves Pass 01/04 usable.
      const [dimsR, pxR] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/api/v1/expert/prescriptions/dims?category=${category}&profile_id=${profileId}`, { headers }),
        axios.get(`${API_BASE_URL}/api/v1/expert/prescriptions?profile_id=${profileId}&category=${category}`, { headers }),
      ]);
      if (dimsR.status === 'fulfilled' && pxR.status === 'fulfilled') {
        setPrescribeUnavailable(false);
        setPxDims({ shaft: dimsR.value.data.shaft, head: dimsR.value.data.head });
        setPrescriptions(pxR.value.data.prescriptions);
      } else {
        console.warn('Prescribe/score unavailable for this category:',
          dimsR.status === 'rejected' ? dimsR.reason : (pxR as PromiseRejectedResult).reason);
        setPrescribeUnavailable(true);
        setPxDims({ shaft: [], head: [] });
        setPrescriptions([]);
      }
    } catch (err) {
      console.error("Error loading Fitter's Corner:", err);
      if (axios.isAxiosError(err) && err.response?.status === 404 && !resumeProfileId) {
        // Queue exhausted -- not an error. Every golfer in the pool is finished or skipped.
        setQueueEmpty(true);
      } else {
        setError(
          axios.isAxiosError(err) && err.response
            ? `The server returned ${err.response.status} loading the bench.`
            : 'Could not reach the server to load the bench.'
        );
      }
    } finally {
      setLoading(false);
    }
  }, [authHeaders, resumeProfileId, category, skipIds]);

  useEffect(() => { load(); }, [load]);

  // G1 fix: the missing exit. Declare the fitting done (server derives how far the
  // fitter got), then drop straight into the next golfer. Reachable from an
  // incomplete fitting too, per D1 -- a fitter who stopped at Pass 02 can still finish.
  const finishFitting = useCallback(async () => {
    if (!profile) return;
    setBusy(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/v1/expert/fittings/complete`,
        { profile_id: profile.id, category },
        { headers: authHeaders() },
      );
    } catch (err) {
      console.error('Error finishing fitting:', err);
    } finally {
      setBusy(false);
    }
    setPass('cut');
    setReveal(null);
    // A resumed session is on a ?profile_id= URL; clear it so the queue serves next.
    if (resumeProfileId) {
      navigate(`/expert/bench${category === 'driver' ? '' : `?category=${category}`}`);
    } else {
      load();
    }
  }, [profile, category, authHeaders, resumeProfileId, navigate, load]);

  // D3: skip-for-now. Push the id to the per-browser list and refetch.
  const skipGolfer = useCallback(() => {
    if (!profile) return;
    const next = Array.from(new Set([...skipIds, profile.id]));
    try { localStorage.setItem(SKIP_KEY, JSON.stringify(next)); } catch { /* private mode */ }
    setPass('cut');
    setReveal(null);
    setSkipIds(next);
    if (resumeProfileId) {
      navigate(`/expert/bench${category === 'driver' ? '' : `?category=${category}`}`);
    }
  }, [profile, skipIds, resumeProfileId, navigate, category]);

  useEffect(() => {
    if (pass !== 'reveal' || !profile) return;
    setRevealLoading(true);
    axios.get(`${API_BASE_URL}/api/v1/expert/reveal?profile_id=${profile.id}&category=${category}`, { headers: authHeaders() })
      .then(res => setReveal(res.data))
      .catch(err => console.error('Error loading reveal:', err))
      .finally(() => setRevealLoading(false));
  }, [pass, profile, category, authHeaders]);

  const dims = useMemo(() => buildDims(shafts, heads), [shafts, heads]);

  const ruleFor = (target: 'shaft' | 'head', dimension: string, opt: DimOption) =>
    rules.find(r => r.target === target && r.dimension === dimension &&
      (opt.operator === 'between'
        ? Array.isArray(r.value) && r.value[0] === opt.value[0] && r.value[1] === opt.value[1]
        : r.value === opt.value));

  const poolCount = (target: 'shaft' | 'head') => {
    const items: (ShaftRow | HeadRow)[] = target === 'shaft' ? shafts : heads;
    const unplayable = rules.filter(r => r.target === target && r.band_low === 0);
    return items.filter(it => !unplayable.some(r => matchesRule(target, r.dimension, r.operator, r.value, it))).length;
  };
  const shaftsLive = poolCount('shaft');
  const headsLive = poolCount('head');

  const handleChipClick = async (target: 'shaft' | 'head', dimension: string, opt: DimOption) => {
    const existing = ruleFor(target, dimension, opt);
    if (existing) {
      setBusy(true);
      try {
        await axios.delete(`${API_BASE_URL}/api/v1/expert/rules/${existing.id}`, { headers: authHeaders() });
        setRules(prev => prev.filter(r => r.id !== existing.id));
      } catch (err) {
        console.error('Error removing rule:', err);
      } finally {
        setBusy(false);
      }
      return;
    }
    setArmed({ target, dimension, opt });
  };

  const setBand = async (bandIndex: number) => {
    if (!armed || !profile) return;
    const { target, dimension, opt } = armed;
    const band = BANDS[bandIndex];
    setBusy(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/v1/expert/rules`, {
        profile_id: profile.id,
        category,
        target,
        dimension,
        operator: opt.operator,
        value: opt.value,
        action: 'cut',
        reason: `${dims.find(d => d.target === target && d.dimension === dimension)?.name}: ${opt.label}`,
        band_low: band.lo,
        band_high: band.hi,
      }, { headers: authHeaders() });
      setRules(prev => [...prev, res.data]);
    } catch (err) {
      console.error('Error creating rule:', err);
    } finally {
      setBusy(false);
      setArmed(null);
    }
  };

  const dropRule = async (rule: FitRule) => {
    setBusy(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/v1/expert/rules/${rule.id}`, { headers: authHeaders() });
      setRules(prev => prev.filter(r => r.id !== rule.id));
    } catch (err) {
      console.error('Error removing rule:', err);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setArmed(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const pxFor = (target: 'shaft' | 'head', dimension: string) =>
    prescriptions.find(p => p.target === target && p.dimension === dimension);

  // Upsert one dimension — ported from setPx(). Always sends the full row so a
  // partial patch (e.g. just `importance`) can't clobber levels/ideal already set.
  const postPx = async (target: 'shaft' | 'head', D: PxDimMeta, patch: Partial<Prescription>) => {
    if (!profile) return;
    const existing = pxFor(target, D.dimension);
    const body = {
      profile_id: profile.id,
      category,
      target,
      dimension: D.dimension,
      kind: D.kind,
      ideal: existing?.ideal ?? null,
      range_low: existing?.range_low ?? null,
      range_high: existing?.range_high ?? null,
      levels: existing?.levels ?? null,
      primary_level: existing?.primary_level ?? null,
      sub_specs: existing?.sub_specs ?? null,
      importance: existing?.importance ?? 1,
      no_view: existing?.no_view ?? false,
      ...patch,
    };
    setBusy(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/v1/expert/prescriptions`, body, { headers: authHeaders() });
      setPrescriptions(prev => [...prev.filter(p => !(p.target === target && p.dimension === D.dimension)), res.data]);
    } catch (err) {
      console.error('Error saving prescription:', err);
    } finally {
      setBusy(false);
    }
  };

  const clearPx = async (target: 'shaft' | 'head', D: PxDimMeta) => {
    const existing = pxFor(target, D.dimension);
    if (!existing) return;
    setBusy(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/v1/expert/prescriptions/${existing.id}`, { headers: authHeaders() });
      setPrescriptions(prev => prev.filter(p => p.id !== existing.id));
    } catch (err) {
      console.error('Error clearing prescription:', err);
    } finally {
      setBusy(false);
    }
  };

  // select -> promote to primary -> deselect. Matches JS tglLevel().
  const toggleLevel = (target: 'shaft' | 'head', D: PxDimMeta, level: string) => {
    const existing = pxFor(target, D.dimension);
    const levels = existing?.levels ? [...existing.levels] : [];
    let primary = existing?.primary_level ?? null;
    let subSpecs = existing?.sub_specs ? { ...existing.sub_specs } : null;
    if (!levels.includes(level)) {
      levels.push(level);
      if (!primary) primary = level;
    } else if (primary !== level) {
      primary = level;
    } else {
      const idx = levels.indexOf(level);
      levels.splice(idx, 1);
      primary = levels[0] ?? null;
      if (subSpecs) { subSpecs = { ...subSpecs }; delete subSpecs[level]; }
    }
    postPx(target, D, { levels, primary_level: primary, sub_specs: subSpecs });
  };

  const setSubRange = (target: 'shaft' | 'head', D: PxDimMeta, level: string, subDim: string, range: [number, number]) => {
    const existing = pxFor(target, D.dimension);
    const subSpecs = existing?.sub_specs ? { ...existing.sub_specs } : {};
    subSpecs[level] = { ...(subSpecs[level] || {}), [subDim]: range };
    postPx(target, D, { sub_specs: subSpecs });
  };

  // Fetch X3's stratified candidate set once per target, the first time Pass 03
  // needs it — cached in state after that so placing/moving scores doesn't
  // re-stratify the set out from under the fitter mid-pass.
  useEffect(() => {
    if (pass !== 'score' || !profile || prescribeUnavailable || candidatesLoaded[scoreTarget]) return;
    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/v1/expert/candidates?profile_id=${profile.id}&category=${category}&target=${scoreTarget}`,
          { headers: authHeaders() }
        );
        setCandidates(prev => ({ ...prev, [scoreTarget]: res.data.candidates }));
        setCandidatesLoaded(prev => ({ ...prev, [scoreTarget]: true }));
      } catch (err) {
        console.error('Error loading candidates:', err);
      }
    })();
  }, [pass, scoreTarget, profile, category, prescribeUnavailable, candidatesLoaded, authHeaders]);

  const scoreFor = (target: 'shaft' | 'head', itemId: number) =>
    scores.find(s => s.target === target && s.item_id === itemId);

  // Place (or move) one candidate on the strip — ported from place(). Sends
  // this candidate's slice/distance, frozen at placement time per §0.8.
  const place = async (target: 'shaft' | 'head', c: Candidate, rawValue: number) => {
    if (!profile) return;
    const value = Math.max(0, Math.min(10, Math.round(rawValue * 2) / 2));
    setBusy(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/v1/expert/scores`, {
        profile_id: profile.id,
        category,
        target,
        item_id: c.item_id,
        score: value,
        slice: c.slice,
        distance: c.distance,
        is_anchor: c.is_anchor,
      }, { headers: authHeaders() });
      setScores(prev => [...prev.filter(s => !(s.target === target && s.item_id === c.item_id)), res.data]);
    } catch (err) {
      console.error('Error placing score:', err);
    } finally {
      setBusy(false);
      setArmedScore(null);
    }
  };

  const unplace = async (target: 'shaft' | 'head', itemId: number) => {
    const existing = scoreFor(target, itemId);
    if (!existing) return;
    setBusy(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/v1/expert/scores/${existing.id}`, { headers: authHeaders() });
      setScores(prev => prev.filter(s => s.id !== existing.id));
    } catch (err) {
      console.error('Error unplacing score:', err);
    } finally {
      setBusy(false);
      setArmedScore(itemId);
    }
  };

  const scoreTray = candidates[scoreTarget].filter(c => !scoreFor(scoreTarget, c.item_id));

  // Keyboard placement — ported from the prototype's Pass 03 keydown handler.
  // 1-9 arms the nth tray item, 0-9 places at that value once armed, Q places 10.
  useEffect(() => {
    if (pass !== 'score') return;
    const onKey = (e: KeyboardEvent) => {
      const tgt = e.target as HTMLElement;
      if (tgt && tgt.matches && tgt.matches('input,textarea,select')) return;
      if (armedScore == null && /^[1-9]$/.test(e.key)) {
        const c = scoreTray[+e.key - 1];
        if (c) { setArmedScore(c.item_id); e.preventDefault(); }
        return;
      }
      if (armedScore != null) {
        const c = candidates[scoreTarget].find(x => x.item_id === armedScore);
        if (!c) return;
        if (/^[0-9]$/.test(e.key)) { place(scoreTarget, c, +e.key); e.preventDefault(); }
        else if (e.key === 'q' || e.key === 'Q') { place(scoreTarget, c, 10); e.preventDefault(); }
        else if (e.key === 'Escape') { setArmedScore(null); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pass, armedScore, scoreTarget, candidates, scoreTray]);

  // Pass 04 (X4b, §0.11 R1) — heads he scored, best first: the matrix's rows.
  // Sourced from the same expert_item_scores this expert already placed in
  // Pass 03, not a fresh pull off the full catalog — the matrix composes what
  // was already vetted, it doesn't re-open the whole pool.
  const scoredHeads = useMemo(() => {
    return scores
      .filter(s => s.target === 'head')
      .slice()
      .sort((a, b) => b.score - a.score)
      .map(s => ({ score: s, head: heads.find(h => h.id === s.item_id) }))
      .filter((x): x is { score: ItemScore; head: HeadRow } => !!x.head);
  }, [scores, heads]);

  // The shaft palette — a copy source (§0.11 R1: "must stay in the palette and
  // be usable under every other head"), so this list never filters out a shaft
  // already placed under the active head.
  const scoredShafts = useMemo(() => {
    return scores
      .filter(s => s.target === 'shaft')
      .slice()
      .sort((a, b) => b.score - a.score)
      .map(s => ({ score: s, shaft: shafts.find(sh => sh.id === s.item_id) }))
      .filter((x): x is { score: ItemScore; shaft: ShaftRow } => !!x.shaft);
  }, [scores, shafts]);

  const matrixRows = buildScores.filter(b => b.source === 'matrix' || b.source === 'probe');
  const combosForHead = (headId: number) => matrixRows.filter(b => b.head_id === headId);
  const comboFor = (headId: number, shaftId: number) =>
    matrixRows.find(b => b.head_id === headId && b.shaft_id === shaftId);
  const idealRows = buildScores.filter(b => b.source === 'ideal');
  const idealFor = (rowOrder: number) => idealRows.find(b => b.row_order === rowOrder);

  const postBuild = async (body: Partial<BuildScore> & { source: 'matrix' | 'ideal' | 'probe' }) => {
    if (!profile) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/v1/expert/builds`, {
        profile_id: profile.id,
        category,
        head_id: body.head_id ?? null,
        shaft_id: body.shaft_id ?? null,
        score: body.score,
        source: body.source,
        rank_in_head: body.rank_in_head ?? null,
        head_loft: body.head_loft ?? null,
        shaft_flex: body.shaft_flex ?? null,
        row_order: body.row_order ?? null,
      }, { headers: authHeaders() });
      setBuildScores(prev => {
        const withoutThis = body.source === 'ideal'
          ? prev.filter(b => !(b.source === 'ideal' && b.row_order === body.row_order))
          : prev.filter(b => !((b.source === 'matrix' || b.source === 'probe') && b.head_id === body.head_id && b.shaft_id === body.shaft_id));
        return [...withoutThis, res.data];
      });
      return res.data as BuildScore;
    } catch (err) {
      console.error('Error saving build score:', err);
    } finally {
      setBusy(false);
    }
  };

  const deleteBuild = async (id: number) => {
    setBusy(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/v1/expert/builds/${id}`, { headers: authHeaders() });
      setBuildScores(prev => prev.filter(b => b.id !== id));
    } catch (err) {
      console.error('Error removing build score:', err);
    } finally {
      setBusy(false);
    }
  };

  // Toggle one shaft under the active head — click or keyboard (digit) both
  // route here, satisfying §0.11 R1's "keyboard path is mandatory". Adding
  // assigns the next open rank (up to 5); toggling an already-placed shaft off
  // removes it. New combos default to score 5 (Workable) — the fitter always
  // adjusts it, this just avoids writing a NULL-shaped score.
  const toggleMatrixShaft = (headId: number, shaftId: number) => {
    const existing = comboFor(headId, shaftId);
    if (existing) {
      deleteBuild(existing.id);
      return;
    }
    const used = combosForHead(headId);
    if (used.length >= 5) return;
    const rank = (used.map(u => u.rank_in_head || 0).reduce((m, r) => Math.max(m, r), 0)) + 1;
    postBuild({ head_id: headId, shaft_id: shaftId, score: 5, rank_in_head: rank, source: probeMode ? 'probe' : 'matrix' });
    setProbeMode(false);
  };

  const rescoreCombo = (combo: BuildScore, score: number) => {
    if (!combo.head_id || !combo.shaft_id) return;
    postBuild({ head_id: combo.head_id, shaft_id: combo.shaft_id, score, rank_in_head: combo.rank_in_head, source: combo.source });
  };

  // Keyboard path for the matrix (§0.11 R1 finding 6): with a head row active,
  // 1-9 toggles the nth palette shaft into/out of that head's list.
  useEffect(() => {
    if (pass !== 'build' || activeHeadId == null) return;
    const onKey = (e: KeyboardEvent) => {
      const tgt = e.target as HTMLElement;
      if (tgt && tgt.matches && tgt.matches('input,textarea,select')) return;
      if (/^[1-9]$/.test(e.key)) {
        const p = scoredShafts[+e.key - 1];
        if (p) { toggleMatrixShaft(activeHeadId, p.shaft.id); e.preventDefault(); }
      } else if (e.key === 'Escape') {
        setActiveHeadId(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pass, activeHeadId, scoredShafts, buildScores, probeMode]);

  // Ideal-fit picker (R2): commit a slot once it has a head, a shaft and a
  // score. `head_loft`/`shaft_flex` are read straight off the chosen catalog
  // row rather than asked separately — heads/shafts here are already
  // model x loft / model x flex rows, so the loft/flex is implied by the id.
  // Head and shaft picks land in `idealDraft` first (see its declaration) so
  // picking one, then the other, doesn't lose the first — only once both are
  // present does this write to the server.
  const setIdealSlot = (rowOrder: number, patch: { head_id?: number | null; shaft_id?: number | null; score?: number }) => {
    const existing = idealFor(rowOrder);
    const draft = idealDraft[rowOrder];
    const headId = patch.head_id !== undefined ? patch.head_id : (draft?.head_id ?? existing?.head_id ?? null);
    const shaftId = patch.shaft_id !== undefined ? patch.shaft_id : (draft?.shaft_id ?? existing?.shaft_id ?? null);
    const score = patch.score !== undefined ? patch.score : (existing?.score ?? 5);

    if (headId == null || shaftId == null) {
      setIdealDraft(prev => ({ ...prev, [rowOrder]: { head_id: headId, shaft_id: shaftId } }));
      // Not enough to write yet; if a row already exists (e.g. head cleared), drop it.
      if (existing) deleteBuild(existing.id);
      return;
    }
    setIdealDraft(prev => { const next = { ...prev }; delete next[rowOrder]; return next; });
    const head = heads.find(h => h.id === headId);
    const shaft = shafts.find(s => s.id === shaftId);
    postBuild({
      head_id: headId, shaft_id: shaftId, score, row_order: rowOrder, source: 'ideal',
      head_loft: head?.loft ?? null, shaft_flex: shaft?.standard_flex ?? null,
    });
  };

  const clearIdealSlot = (rowOrder: number) => {
    setIdealDraft(prev => { const next = { ...prev }; delete next[rowOrder]; return next; });
    const existing = idealFor(rowOrder);
    if (existing) deleteBuild(existing.id);
  };

  if (loading) {
    return (
      <div className="bench">
        <div className="bench-loading"><GolfLoadingSpinner message="Loading the bench..." size="large" /></div>
      </div>
    );
  }

  if (queueEmpty) {
    return (
      <div className="bench">
        <div className="bench-empty-state" style={{ maxWidth: 460, margin: '15vh auto', textAlign: 'center' }}>
          <h2 style={{ marginBottom: 8 }}>You've fitted every golfer available.</h2>
          <p style={{ color: 'var(--ink-2)' }}>
            There's nothing left in the {category} queue{skipIds.length ? ' (skipped golfers aside)' : ''}.
          </p>
          <button className="bench-btn pri" onClick={() => navigate('/expert')} style={{ marginTop: 16 }}>
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="bench">
        <div className="bench-error">
          <p>{error || 'No profile available.'}</p>
          <button className="bench-btn pri" onClick={() => navigate('/expert')} style={{ marginTop: 12 }}>
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  const renderDim = (D: DimConfig) => {
    const catalog: (ShaftRow | HeadRow)[] = D.target === 'shaft' ? shafts : heads;
    const isArmedHere = armed && armed.target === D.target && armed.dimension === D.dimension;
    return (
      <div className="bench-dim" key={`${D.target}-${D.dimension}`}>
        <div className="bench-dimhead">
          <span className="nm">{D.name}</span>
          <span className={`bench-prov${D.warn ? ' warn' : ''}`}>{D.prov}</span>
        </div>
        <div className={`bench-chips${D.dimension === 'manufacturer' || D.dimension === 'material' ? ' scroll' : ''}`}>
          {D.opts.map(opt => {
            const n = catalog.filter(it => matchesRule(D.target, D.dimension, opt.operator, opt.value, it)).length;
            if (!n) return null;
            const existing = ruleFor(D.target, D.dimension, opt);
            return (
              <button
                key={opt.key}
                className="bench-chip"
                data-band={existing ? BANDS.findIndex(b => b.lo === existing.band_low) : undefined}
                disabled={busy}
                aria-pressed={!!existing}
                onClick={() => handleChipClick(D.target, D.dimension, opt)}
              >
                {opt.label}<span className="n">{n}</span>
              </button>
            );
          })}
        </div>
        {isArmedHere && (
          <div className="bench-bandpick">
            <span className="q">&ldquo;{armed!.opt.label}&rdquo; is &mdash;</span>
            {BANDS.map(b => (
              <button key={b.i} className="bench-bandbtn" data-b={b.i} disabled={busy} onClick={() => setBand(b.i)}>
                {b.name}<span className="rng">{b.lo}&ndash;{b.hi}</span>
              </button>
            ))}
            <button className="bench-bandbtn" style={{ color: 'var(--ink-3)' }} onClick={() => setArmed(null)}>cancel</button>
          </div>
        )}
      </div>
    );
  };

  const pxSummary = (target: 'shaft' | 'head', D: PxDimMeta): string | null => {
    const v = pxFor(target, D.dimension);
    if (!v || v.no_view) return null;
    if (D.kind === 'continuous') {
      if (v.ideal == null) return null;
      return `${v.range_low}–${v.range_high}${D.unit}, ideal ${v.ideal}${D.unit}`;
    }
    if (v.levels && v.levels.length) {
      let s = v.levels.map(l => (l === v.primary_level ? l + '★' : l)).join(' / ');
      if (v.sub_specs) {
        for (const k of Object.keys(v.sub_specs)) {
          const e = Object.entries(v.sub_specs[k]).map(([sd, r]) => `${sd} ${r[0]}–${r[1]}`).join(', ');
          if (e) s += ` · ${k}: ${e}`;
        }
      }
      return s;
    }
    return null;
  };

  const renderPxDim = (target: 'shaft' | 'head') => (D: PxDimMeta) => {
    const v = pxFor(target, D.dimension);
    const sum = pxSummary(target, D);
    return (
      <div className="bench-dim" key={`px-${target}-${D.dimension}`}>
        <div className="bench-dimhead">
          <span className="nm">{D.name}</span>
          {D.provenance && <span className={`bench-prov${D.warn ? ' warn' : ''}`}>{D.provenance}</span>}
          <span className={`bench-pxval${sum ? '' : ' unset'}`}>
            {sum || (v?.no_view ? 'no constraint recorded' : 'not set')}
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
            <button
              className={`bench-star${v?.importance === 2 ? ' on' : ''}`}
              title="critical — doubles retrieval weight"
              disabled={busy}
              onClick={() => postPx(target, D, { importance: v?.importance === 2 ? 1 : 2 })}
            >&#9733;</button>
            <button
              className={`bench-noview${v?.no_view ? ' on' : ''}`}
              disabled={busy}
              onClick={() => postPx(target, D, { no_view: !v?.no_view })}
            >no view</button>
            {v && <button aria-label={`clear ${D.name}`} disabled={busy} onClick={() => clearPx(target, D)}>&times;</button>}
          </span>
        </div>
        {v?.no_view ? (
          <div className="hint">Recorded as <strong>no constraint</strong> &mdash; not a default range.</div>
        ) : D.kind === 'continuous' ? (
          <RangeSlider D={D} value={v} busy={busy}
            onCommit={(range_low, range_high, ideal) => postPx(target, D, { range_low, range_high, ideal })} />
        ) : (
          <>
            <div className="bench-levels">
              {(D.levels || []).map(l => {
                const on = (v?.levels || []).includes(l);
                return (
                  <button
                    key={l}
                    className={`bench-lv${on ? ' on' : ''}${v?.primary_level === l ? ' primary' : ''}`}
                    disabled={busy}
                    onClick={() => toggleLevel(target, D, l)}
                  >
                    {l}<span className="c">{D.counts?.[l] ?? ''}</span>
                    {v?.primary_level === l && <span className="pri">PRIMARY</span>}
                  </button>
                );
              })}
            </div>
            {(v?.levels || []).length > 0 && (
              <div className="hint" style={{ margin: '7px 0 0' }}>
                Click once to take it, again to make it the one he leans to, a third time to drop it.
                Primary: <strong style={{ color: 'var(--fitter)' }}>{v?.primary_level || 'none'}</strong>.
              </div>
            )}
            {D.subs && (v?.levels || []).map(l => (
              <div className="bench-subspec" key={l}>
                <div className="sh">if <strong>{l}</strong>, it has to be &mdash;</div>
                {D.subs!.map(sb => {
                  const r = (v?.sub_specs || {})[l]?.[sb.dimension] || sb.ext;
                  return (
                    <div key={sb.dimension} style={{ marginTop: 6 }}>
                      <span className="lbl">{sb.name}</span>
                      <SubRangeInput ext={sb.ext} value={r} busy={busy}
                        onCommit={(lo, hi) => setSubRange(target, D, l, sb.dimension, [lo, hi])} />
                    </div>
                  );
                })}
              </div>
            ))}
          </>
        )}
        {D.note && <div className="hint" style={{ marginTop: 8, color: 'var(--engine)' }}>{D.note}</div>}
      </div>
    );
  };

  const pxSetCount = (target: 'shaft' | 'head') =>
    prescriptions.filter(p => p.target === target && (p.no_view || p.levels?.length || p.ideal != null)).length;

  // Pass 03 ("score the strip") — ported from renderScore(). Lane-packs placed
  // chips so nearby scores don't overlap, mirroring the prototype's lane logic.
  const renderScorePass = () => {
    const list = candidates[scoreTarget];
    const placed = list.filter(c => scoreFor(scoreTarget, c.item_id));
    const tray = scoreTray;
    const hasCurrent = list.some(c => c.slice === 'current');

    // C3: chips are capped at 170px wide; the collision threshold has to be
    // that pixel width expressed as a percentage of the *measured* strip, not
    // a constant percentage — a constant only avoids overlap once the strip
    // happens to be wide enough (§4.1: ~1228px+ for the old hard-coded 26).
    const CHIP_PX = 175; // chip max-width (170) + a little breathing room
    const LANE_PX = 46; // vertical pitch between stacked lanes; chips are now min-height:44px (M2)
    const thresholdPct = stripWidth > 0 ? (CHIP_PX / stripWidth) * 100 : 26;
    const lanes: number[] = [];
    let maxLane = 0;
    const sortedPlaced = [...placed].sort(
      (a, b) => (scoreFor(scoreTarget, a.item_id)!.score) - (scoreFor(scoreTarget, b.item_id)!.score)
    );
    const chipPositions = sortedPlaced.map(c => {
      const v = scoreFor(scoreTarget, c.item_id)!.score;
      const x = v * 10;
      let lane = 0;
      while (lanes[lane] != null && Math.abs(lanes[lane] - x) < thresholdPct) lane++;
      lanes[lane] = x;
      maxLane = Math.max(maxLane, lane);
      const shift = x < 14 ? '0' : x > 86 ? '-100%' : '-50%';
      return { c, v, x, lane, shift };
    });

    const armedC = armedScore != null ? list.find(c => c.item_id === armedScore) : undefined;

    return (
      <>
        <div className="bench-head">
          <span className="num">03</span>
          <h1>{scoreTarget === 'shaft' ? 'Score the shafts' : 'Score the heads'}</h1>
          <p>
            Twelve {scoreTarget}s, drawn at graded distances from what you just prescribed &mdash; on
            target, near, further out, one already cut, and his current club. The spread is
            deliberate: a training set of nothing but good clubs teaches the model nothing.
          </p>
        </div>

        {/* M1: computer-only tool, no tablet/phone layout -- below a sane
            minimum window width, say so instead of rendering something
            broken. Hidden by default; shown only by the narrow-viewport
            media query. */}
        <div className="bench-card bench-narrow-notice" style={{ borderColor: 'var(--engine)' }}>
          <strong>This window is too narrow for the bench.</strong> Fitter's Corner is a computer
          tool &mdash; widen the browser window to score the strip.
        </div>

        <div className="bench-stripwrap">
          <div className="bench-bands">
            {SCORE_BANDS.map(b => <div key={b.i} className="bench-bd" data-b={b.i}>{b.name}</div>)}
          </div>
          <div
            ref={stripRef}
            className={`bench-strip${armedScore != null ? ' armed' : ''}`}
            style={{ height: Math.max(120, (maxLane + 1) * LANE_PX + 14) }}
            onClick={e => {
              if (armedScore == null || (e.target as HTMLElement).closest('.bench-pl')) return;
              const c = list.find(x => x.item_id === armedScore);
              if (!c) return;
              const r = e.currentTarget.getBoundingClientRect();
              place(scoreTarget, c, ((e.clientX - r.left) / r.width) * 10);
            }}
          >
            <div className="bench-ticks">
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
                <div key={i} className="bench-tick" style={{ left: `${i * 10}%` }}><b>{i}</b></div>
              ))}
            </div>
            {chipPositions.map(({ c, v, x, lane, shift }) => (
              <button
                key={c.item_id}
                className="bench-pl"
                data-slice={c.slice}
                style={{ left: `${x}%`, top: lane * LANE_PX, transform: `translateX(${shift})` }}
                disabled={busy}
                title={`${c.label} — click to move`}
                onClick={() => unplace(scoreTarget, c.item_id)}
              >
                <span className="sc" style={{ color: scoreBandColor(v) }}>{v}</span>
                <span className="nm">{c.label}</span>
                {c.slice === 'current' && <span className="tagx">NOW</span>}
                {c.slice === 'cut' && <span className="tagx">CUT</span>}
              </button>
            ))}
          </div>
          <div className="bench-stripfoot">
            <span>{placed.length} of {list.length} placed</span>
            <span>ties are legal &mdash; put the S and the X on the same number if that's the truth</span>
          </div>
        </div>

        {!hasCurrent && (
          <div className="bench-card" style={{ borderColor: 'var(--engine)' }}>
            <strong>No anchor available.</strong> We cannot identify the {scoreTarget} he plays now
            {scoreTarget === 'shaft' ? ' — current_driver_shaft_model is populated on only 19% of real profiles, and it is empty here' : ''}.
            The scale loses its fixed point, so these scores are harder to compare against another fitter's.
          </div>
        )}

        {armedC ? (
          <div className="bench-armbar">
            <b>{armedC.label}</b> armed &mdash; click the strip, or press <b>0</b>&ndash;<b>9</b>, or <b>Q</b> for 10.
            <button className="bench-btn" style={{ marginLeft: 'auto', padding: '3px 9px' }} onClick={() => setArmedScore(null)}>cancel</button>
          </div>
        ) : (
          <p className="hint" style={{ marginTop: 10 }}>Pick one below (or press its number), then place it.</p>
        )}

        <div className="bench-tray">
          {tray.length ? tray.map((c, i) => (
            <button
              key={c.item_id}
              className={`bench-ti${armedScore === c.item_id ? ' armed' : ''}`}
              disabled={busy}
              onClick={() => setArmedScore(armedScore === c.item_id ? null : c.item_id)}
            >
              <span className="t">{c.label}</span>
              <span className="s">{c.spec}</span>
              {i < 9 && <span className="kb">{i + 1}</span>}
            </button>
          )) : <span className="bench-empty">All placed.</span>}
        </div>

        <div className="bench-nav">
          <button className="bench-btn" onClick={() => setPass('prescribe')}>&larr; Prescribe</button>
          {scoreTarget === 'shaft' ? (
            <button
              className="bench-btn pri"
              disabled={tray.length > 0}
              onClick={() => setScoreTarget('head')}
            >
              Score heads &rarr;
            </button>
          ) : (
            <button className="bench-btn pri" disabled={tray.length > 0} onClick={() => setPass('build')}>Build &rarr;</button>
          )}
          <span className="bench-navnote">
            {tray.length ? `${tray.length} left — every candidate needs a score, including the one you cut.` : 'All placed.'}
          </span>
        </div>
      </>
    );
  };

  // Pass 04 ("build the shortlist") — X4b, §0.11 R1+R2. No prototype to port;
  // see the block comment near BuildScore's declaration for the design.
  const renderBuildPass = () => {
    return (
      <>
        <div className="bench-head">
          <span className="num">04</span>
          <h1>Build the shortlist</h1>
          <p>
            Two surfaces. First, a matrix: your scored heads down the left, up to five of
            your scored shafts under each one, ranked and scored as a combo. Second, an
            ideal-fit picker &mdash; up to ten specific builds, head loft and shaft flex
            included, scored fine enough to tell 9.1 from 9.2.
          </p>
        </div>

        {scoredHeads.length === 0 || scoredShafts.length === 0 ? (
          <div className="bench-card" style={{ borderColor: 'var(--engine)' }}>
            <strong>Nothing to build from yet.</strong> The matrix composes what you already
            scored in Pass 03 &mdash; score at least one head and one shaft there first.
          </div>
        ) : (
          <div className="bench-card">
            <h3>Build matrix</h3>
            <p className="hint">
              Click a head to select it, then click a palette shaft to place it under that
              head (or press its number). Click a placed shaft to remove it. The palette is
              a copy source &mdash; a shaft placed under one head stays available for every
              other head.
            </p>

            <div className="bench-matrix">
              {scoredHeads.map(({ score: hs, head }) => {
                const combos = combosForHead(head.id).slice().sort((a, b) => (a.rank_in_head || 0) - (b.rank_in_head || 0));
                const isActive = activeHeadId === head.id;
                return (
                  <div className={`bench-mxrow${isActive ? ' active' : ''}`} key={head.id}>
                    <button
                      className="bench-mxhead"
                      onClick={() => setActiveHeadId(isActive ? null : head.id)}
                    >
                      <span className="sc" style={{ color: scoreBandColor(hs.score) }}>{hs.score}</span>
                      <span className="nm">{head.brand} {head.model}</span>
                      <span className="c">{combos.length}/5</span>
                    </button>
                    <div className="bench-mxcombos">
                      {combos.length ? combos.map(c => (
                        <div className={`bench-mxcombo${c.source === 'probe' ? ' probe' : ''}`} key={c.id}>
                          <div className="bench-mxcombotop">
                            <span className="rk">{c.rank_in_head}</span>
                            <span className="nm">{shafts.find(s => s.id === c.shaft_id)?.brand} {shafts.find(s => s.id === c.shaft_id)?.model}</span>
                            {c.source === 'probe' && <span className="tagx">PROBE</span>}
                            <button aria-label="remove combo" disabled={busy} onClick={() => deleteBuild(c.id)}>&times;</button>
                          </div>
                          <div className="bench-scorerow">
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                              <button
                                key={n}
                                className={`bench-sb${c.score === n ? ' on' : ''}`}
                                style={c.score === n ? { background: scoreBandColor(n), borderColor: scoreBandColor(n) } : undefined}
                                disabled={busy}
                                onClick={() => rescoreCombo(c, n)}
                              >
                                {n}
                              </button>
                            ))}
                          </div>
                        </div>
                      )) : <span className="bench-empty">No shafts placed under this head yet.</span>}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="bench-mxpalette">
              <div className="bench-dimhead">
                <span className="nm">Shaft palette</span>
                <label className="bench-probetoggle">
                  <input type="checkbox" checked={probeMode} onChange={e => setProbeMode(e.target.checked)} disabled={activeHeadId == null} />
                  next placement is the deliberate mismatch
                </label>
              </div>
              {activeHeadId == null ? (
                <p className="hint">Select a head above to place shafts under it.</p>
              ) : (
                <div className="bench-chips">
                  {scoredShafts.map(({ score: ss, shaft }, i) => {
                    const placed = !!comboFor(activeHeadId, shaft.id);
                    return (
                      <button
                        key={shaft.id}
                        className="bench-chip"
                        aria-pressed={placed}
                        disabled={busy}
                        style={placed ? { borderColor: 'var(--fitter)', background: 'var(--fitter-bg)', color: 'var(--fitter)' } : undefined}
                        onClick={() => toggleMatrixShaft(activeHeadId, shaft.id)}
                      >
                        {i < 9 && <span className="kb">{i + 1}</span>}
                        {shaft.brand} {shaft.model}
                        <span className="n" style={{ color: scoreBandColor(ss.score) }}>{ss.score}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="bench-card">
          <h3>Ideal-fit picker</h3>
          <p className="hint">
            Up to ten builds. Repeats are allowed on purpose &mdash; the same head at two
            lofts, the same shaft at two flexes. Score on the slider; a tenth of a point is
            meaningful within this sitting, not across sessions with another fitter.
          </p>
          <div className="bench-ideal">
            {IDEAL_SLOTS.map(rowOrder => {
              const row = idealFor(rowOrder);
              const draft = idealDraft[rowOrder];
              const headId = draft?.head_id ?? row?.head_id ?? null;
              const shaftId = draft?.shaft_id ?? row?.shaft_id ?? null;
              return (
                <div className="bench-idealrow" key={rowOrder}>
                  <span className="idx">{rowOrder}</span>
                  <select
                    className="bench-idealsel" disabled={busy} value={headId ?? ''}
                    onChange={e => setIdealSlot(rowOrder, { head_id: e.target.value ? +e.target.value : null })}
                  >
                    <option value="">head &amp; loft&hellip;</option>
                    {heads.map(h => <option key={h.id} value={h.id}>{h.brand} {h.model} {h.loft != null ? `${h.loft}°` : ''}</option>)}
                  </select>
                  <select
                    className="bench-idealsel" disabled={busy} value={shaftId ?? ''}
                    onChange={e => setIdealSlot(rowOrder, { shaft_id: e.target.value ? +e.target.value : null })}
                  >
                    <option value="">shaft &amp; flex&hellip;</option>
                    {shafts.map(s => <option key={s.id} value={s.id}>{s.brand} {s.model} {s.standard_flex || ''}</option>)}
                  </select>
                  <input
                    type="range" min={0} max={10} step={0.1} className="bench-idealslider"
                    disabled={busy || !row} value={row?.score ?? 5}
                    onChange={e => setIdealSlot(rowOrder, { score: +e.target.value })}
                  />
                  <span className="sc" style={{ color: row ? scoreBandColor(row.score) : 'var(--ink-3)' }}>
                    {row ? row.score.toFixed(1) : '—'}
                  </span>
                  {(row || draft) && <button aria-label={`clear slot ${rowOrder}`} disabled={busy} onClick={() => clearIdealSlot(rowOrder)}>&times;</button>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="bench-nav">
          <button className="bench-btn" onClick={() => { setScoreTarget('head'); setPass('score'); }}>&larr; Score</button>
          <button
            className="bench-btn"
            disabled={matrixRows.length === 0 && idealRows.length === 0}
            onClick={() => navigate(`/expert/bay?profile_id=${profile.id}`)}
            title="Real golfer, launch monitor running -- test these builds and record what each one did"
          >
            Test in the bay &rarr;
          </button>
          <button className="bench-btn pri" onClick={() => setPass('reveal')}>Reveal &rarr;</button>
          <span className="bench-navnote">
            {matrixRows.length} combo{matrixRows.length === 1 ? '' : 's'} scored ·{' '}
            {idealRows.length} of 10 ideal-fit slots filled.
          </span>
        </div>
      </>
    );
  };

  // Pass 05 ("reveal") — X5. No prototype to port beyond §0.10's verified run;
  // this pulls the same numbers for real off GET /expert/reveal.
  const renderRevealPass = () => (
    <>
      <div className="bench-head">
        <span className="num">05</span>
        <h1>Reveal</h1>
        <p>
          Two checks against what you actually did. First: did your own scores fall the way
          the stratified slice predicted &mdash; on target should beat near, near should beat
          cut. Second: for the rules you stated in Pass 01, did the engine agree with your
          call, wherever that's checkable at all.
        </p>
      </div>

      {revealLoading || !reveal ? (
        <div className="bench-card"><span className="bench-empty">Loading&hellip;</span></div>
      ) : (
        <>
          <div className="bench-revealgrid">
            {(['shaft', 'head'] as const).map(target => (
              <div className="bench-card" key={target}>
                <h3>{target === 'shaft' ? 'Shafts' : 'Heads'} &mdash; score by slice</h3>
                {reveal.slices[target].length ? reveal.slices[target].map(row => (
                  <div className="bench-slicebar" key={row.slice}>
                    <span className="nm">{SLICE_LABEL[row.slice] || row.slice}</span>
                    <span className="track">
                      <span className="fill" style={{ width: `${row.mean_score * 10}%`, background: scoreBandColor(row.mean_score) }} />
                    </span>
                    <span className="sc" style={{ color: scoreBandColor(row.mean_score) }}>{row.mean_score.toFixed(1)}</span>
                    <span className="n">n={row.n}</span>
                  </div>
                )) : <div className="bench-empty">Nothing scored yet.</div>}
              </div>
            ))}
          </div>

          <div className="bench-card">
            <h3>Rules vs. the engine</h3>
            <p className="hint">
              Computed at write time against V9's own per-dimension scoring, where checkable
              (driver, per-golfer, a dimension V9 scores on its own). A disagreement here is a
              finding about our rules, not a training label.
            </p>
            {reveal.rules.length ? reveal.rules.map(r => {
              const verdict = r.agrees_with_engine == null ? 'unknown' : r.agrees_with_engine ? 'agree' : 'disagree';
              return (
                <div className="bench-ruleagree" key={r.id}>
                  <span className="nm">{r.reason || `${r.target}.${r.dimension}`}</span>
                  <span className="bench-verdict" data-v={verdict}>
                    {verdict === 'agree' ? 'engine agrees' : verdict === 'disagree' ? 'engine disagrees' : 'not checkable'}
                  </span>
                </div>
              );
            }) : <div className="bench-empty">No rules recorded for this golfer.</div>}
          </div>
        </>
      )}

      <div className="bench-nav">
        <button className="bench-btn" onClick={() => setPass('build')}>&larr; Build</button>
        <button className="bench-btn pri" disabled={busy} onClick={finishFitting} style={{ marginLeft: 'auto' }}>
          Finish fitting &mdash; next golfer &rarr;
        </button>
      </div>
    </>
  );

  // Golfer rail — left column, port of the artifact's §0.9/§0.10 prototype.
  // Provenance dot per field: meas (launch monitor) / self (reported) / derived (computed here).
  const renderRail = () => {
    if (!profile) return null;

    const num = (v: any, d = 1): string | null =>
      v == null || v === '' ? null : Number(v).toFixed(d);
    const sgn = (v: any, d = 1): string | null =>
      v == null || v === '' ? null : `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(d)}`;

    const impliedSmash = profile.ball_speed_driver && profile.swing_speed_driver
      ? profile.ball_speed_driver / profile.swing_speed_driver : null;
    const spinLoft = profile.dynamic_loft_driver != null && profile.attack_angle_driver != null
      ? profile.dynamic_loft_driver - profile.attack_angle_driver : null;

    type Prov = 'meas' | 'self' | 'derived';
    type Field = { k: string; v: string | null; p: Prov };

    const golfer: Field[] = [
      { k: 'Handicap', v: num(profile.handicap), p: 'self' },
      { k: 'Age', v: profile.age != null ? String(profile.age) : null, p: 'self' },
      { k: 'Height', v: profile.height_inches != null ? `${profile.height_inches}"` : null, p: 'self' },
      { k: 'Wrist-to-floor', v: profile.wrist_to_floor != null ? `${profile.wrist_to_floor}"` : null, p: 'self' },
      { k: 'Hand', v: profile.dominant_hand || null, p: 'self' },
      { k: 'Tempo', v: profile.swing_tempo || null, p: 'self' },
      { k: 'Flexibility', v: profile.flexibility_rating != null ? `${profile.flexibility_rating}/10` : null, p: 'self' },
      { k: 'Core strength', v: profile.core_strength != null ? `${profile.core_strength}/10` : null, p: 'self' },
      { k: 'Years playing', v: profile.years_playing != null ? String(profile.years_playing) : null, p: 'self' },
      { k: 'Rounds / yr', v: profile.rounds_per_year != null ? String(profile.rounds_per_year) : null, p: 'self' },
    ];

    const delivery: Field[] = [
      { k: 'Club speed', v: profile.swing_speed_driver != null ? `${profile.swing_speed_driver} mph` : null,
        p: profile.has_launch_monitor_driver ? 'meas' : 'self' },
      { k: 'Attack angle', v: sgn(profile.attack_angle_driver) != null ? `${sgn(profile.attack_angle_driver)}°` : null, p: 'meas' },
      { k: 'Club path', v: sgn(profile.club_path_driver) != null ? `${sgn(profile.club_path_driver)}°` : null, p: 'meas' },
      { k: 'Face angle', v: sgn(profile.face_angle_driver) != null ? `${sgn(profile.face_angle_driver)}°` : null, p: 'meas' },
      { k: 'Face-to-path', v: sgn(profile.face_to_path_driver) != null ? `${sgn(profile.face_to_path_driver)}°` : null, p: 'meas' },
      { k: 'Dynamic loft', v: profile.dynamic_loft_driver != null ? `${profile.dynamic_loft_driver}°` : null, p: 'meas' },
      { k: 'Spin loft', v: spinLoft != null ? `${num(spinLoft)}°` : null, p: 'derived' },
    ];

    const ball: Field[] = [
      { k: 'Ball speed', v: profile.ball_speed_driver != null ? `${profile.ball_speed_driver} mph` : null, p: 'meas' },
      { k: 'Smash (stored)', v: num(profile.smash_factor_driver, 3), p: 'meas' },
      { k: 'Smash (implied)', v: impliedSmash != null ? num(impliedSmash, 3) : null, p: 'derived' },
      { k: 'Launch', v: profile.launch_angle_driver != null ? `${profile.launch_angle_driver}°` : null, p: 'meas' },
      { k: 'Spin', v: profile.spin_rate_driver != null ? `${profile.spin_rate_driver} rpm` : null, p: 'meas' },
      { k: 'Spin axis', v: sgn(profile.spin_axis_driver) != null ? `${sgn(profile.spin_axis_driver)}°` : null, p: 'meas' },
      { k: 'Peak height', v: profile.peak_height_driver != null ? `${profile.peak_height_driver} yd` : null, p: 'meas' },
      { k: 'Descent', v: profile.descent_angle_driver != null ? `${profile.descent_angle_driver}°` : null, p: 'meas' },
      { k: 'Carry', v: profile.driver_carry_distance != null ? `${profile.driver_carry_distance} yd` : null, p: 'meas' },
      { k: 'Total', v: profile.driver_total_distance != null ? `${profile.driver_total_distance} yd` : null, p: 'meas' },
    ];

    const dispersion: Field[] = [
      { k: 'Shots', v: profile.shot_count_driver != null ? String(profile.shot_count_driver) : null, p: 'meas' },
      { k: 'Carry σ', v: profile.carry_std_dev_driver != null ? `±${profile.carry_std_dev_driver} yd` : null, p: 'meas' },
      { k: 'Dispersion σ', v: profile.dispersion_std_dev_driver != null ? `±${profile.dispersion_std_dev_driver} yd` : null, p: 'meas' },
      { k: 'Launch σ', v: profile.launch_angle_std_dev_driver != null ? `±${profile.launch_angle_std_dev_driver}°` : null, p: 'meas' },
      { k: 'Spin σ', v: profile.spin_rate_std_dev_driver != null ? `±${profile.spin_rate_std_dev_driver}` : null, p: 'meas' },
      { k: 'Ball speed σ', v: profile.ball_speed_std_dev_driver != null ? `±${profile.ball_speed_std_dev_driver}` : null, p: 'meas' },
      { k: 'Side deviation', v: profile.side_deviation_driver != null ? `${profile.side_deviation_driver} yd` : null, p: 'meas' },
      { k: 'Miss', v: profile.miss_pattern_driver || null, p: 'self' },
      { k: 'Impact', v: profile.impact_location_pattern_driver || null, p: 'self' },
    ];

    const currentBuild: Field[] = [
      { k: 'Head', v: [profile.current_driver_brand, profile.current_driver_model].filter(Boolean).join(' ') || null, p: 'self' },
      { k: 'Loft', v: profile.current_driver_loft != null ? `${profile.current_driver_loft}°` : null, p: 'self' },
      { k: 'Shaft', v: profile.current_driver_shaft_model || null, p: 'self' },
      { k: 'Flex', v: profile.current_driver_shaft_flex || null, p: 'self' },
      { k: 'Shaft weight', v: profile.current_driver_shaft_weight != null ? `${profile.current_driver_shaft_weight} g` : null, p: 'self' },
      { k: 'Satisfaction', v: profile.current_driver_satisfaction != null ? `${profile.current_driver_satisfaction}/10` : null, p: 'self' },
    ];

    const goals: Field[] = [
      { k: 'Primary goal', v: profile.primary_goal_structured ? String(profile.primary_goal_structured).replace(/_/g, ' ') : null, p: 'self' },
      { k: 'Accuracy priority', v: profile.accuracy_priority != null ? String(profile.accuracy_priority) : null, p: 'self' },
      { k: 'Distance priority', v: profile.distance_priority != null ? String(profile.distance_priority) : null, p: 'self' },
      { k: 'Budget', v: profile.budget_range || null, p: 'self' },
      { k: 'Prefers brands', v: profile.brand_preferences || null, p: 'self' },
    ];

    const renderGroup = (label: string, fields: Field[], open: boolean, extra?: React.ReactNode) => (
      <details className="bench-grp" open={open} key={label}>
        <summary>
          <span className="nm">{label}</span>
          <span className="cnt">{fields.filter(f => f.v != null).length}/{fields.length}</span>
        </summary>
        <div className="bench-fields">
          {fields.map(f => (
            <div className="bench-field" key={f.k}>
              <span className={`p ${f.v == null ? 'miss' : f.p}`} />
              <span className="k">{f.k}</span>
              <span className={`v${f.v == null ? ' na' : ''}`}>{f.v ?? '—'}</span>
            </div>
          ))}
        </div>
        {extra}
      </details>
    );

    const smashStored = profile.smash_factor_driver;
    const smashDisagrees = impliedSmash != null && smashStored != null
      && Math.abs(impliedSmash - smashStored) > 0.005;

    return (
      <>
        <div className="bench-who">
          <h2>Profile #{profile.id}</h2>
          <div className="sub">
            {profile.handicap ?? '—'} hcp &middot; {profile.swing_speed_driver ?? '—'} mph &middot; {profile.gender ?? ''} {profile.age ?? ''}
          </div>
        </div>
        <div className="bench-raillegend">
          <span className="voice"><span className="p meas" style={{ display: 'inline-block' }} /> measured</span>
          <span className="voice"><span className="p self" style={{ display: 'inline-block' }} /> self-reported</span>
          <span className="voice"><span className="p derived" style={{ display: 'inline-block' }} /> derived</span>
          <span className="voice"><span className="p miss" style={{ display: 'inline-block' }} /> missing</span>
        </div>
        {renderGroup('Golfer', golfer, true)}
        {renderGroup('Delivery', delivery, true)}
        {renderGroup('Ball', ball, true, smashDisagrees ? (
          <div className="bench-warnrow">
            <strong>Smash disagrees.</strong> Stored {num(smashStored, 3)}, but {profile.ball_speed_driver} &divide;{' '}
            {profile.swing_speed_driver} = {num(impliedSmash, 3)}. One of the three numbers is wrong.
          </div>
        ) : undefined)}
        {renderGroup('Dispersion', dispersion, false)}
        {renderGroup('Current build', currentBuild, true, (
          <div className="bench-gap">
            <strong>This is the provenance gap.</strong> Across the 129 real profiles: head brand 63%, flex 60%,
            but <strong>shaft model 19%</strong> and <strong>loft 21%</strong>. We mostly cannot say which shaft
            produced the numbers above.
          </div>
        ))}
        {renderGroup('Goals', goals, false)}
      </>
    );
  };

  return (
    <div className="bench" data-theme={theme}>
      <div className="bench-topbar">
        <div className="bench-brand">Fitter's <em>Corner</em></div>
        <div className="bench-voices">
          <span className="bench-voice"><i className="bench-dot f" />fitter</span>
          <span className="bench-voice"><i className="bench-dot e" />engine</span>
          <span className="bench-voice"><i className="bench-dot s" />catalog</span>
        </div>
        <button className="bench-back" onClick={() => navigate('/expert')}>&larr; Back to dashboard</button>
        <button className="bench-back" onClick={skipGolfer} title="Skip this golfer for now (this browser only)">Skip for now</button>
        <button className="bench-back" disabled={busy} onClick={finishFitting} title="Finish this fitting — records how far you got">Finish fitting</button>
        <div className="bench-steps" style={{ marginLeft: 'auto' }}>
          <button className={`bench-step${pass === 'cut' ? ' current' : ' done'}`} onClick={() => setPass('cut')}>
            <span className="n">01</span> Cut
          </button>
          <button className={`bench-step${pass === 'prescribe' ? ' current' : (pass === 'score' || pass === 'build' || pass === 'reveal') ? ' done' : ''}`} onClick={() => setPass('prescribe')}>
            <span className="n">02</span> Prescribe
          </button>
          <button className={`bench-step${pass === 'score' ? ' current' : (pass === 'build' || pass === 'reveal') ? ' done' : ''}`} onClick={() => setPass('score')}>
            <span className="n">03</span> Score
          </button>
          <button className={`bench-step${pass === 'build' ? ' current' : pass === 'reveal' ? ' done' : ''}`} onClick={() => setPass('build')}>
            <span className="n">04</span> Build
          </button>
          <button className={`bench-step${pass === 'reveal' ? ' current' : ''}`} onClick={() => setPass('reveal')}>
            <span className="n">05</span> Reveal
          </button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>{user?.email}</div>
        <button className="bench-tog" onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')}>
          {theme === 'light' ? 'Dark' : 'Light'} mode
        </button>
        <button className="bench-back" onClick={logout}>Log out</button>
      </div>
      <div className="bench-shell">
        <aside className="bench-rail">{renderRail()}</aside>
        <main className="bench-main">
          {pass === 'cut' ? (
            <>
              <div className="bench-head">
                <span className="num">01</span>
                <h1>Cut the bench</h1>
                <p>
                  Everything disqualifying for profile #{profile.id}, stated once as a rule instead of
                  clicked {shafts.length} times. A cut carries a <strong>severity band</strong>, not a
                  boolean &mdash; that's what makes the bottom of the score range usable as training data.
                </p>
              </div>

              <div className="bench-counter">
                <div className="m"><span className="lbl">Shafts live</span>
                  <span className="big">{shaftsLive}<small>/{shafts.length}</small></span></div>
                <div className="m"><span className="lbl">Heads live</span>
                  <span className="big">{headsLive}<small>/{heads.length}</small></span></div>
                <div className="m"><span className="lbl">Rules</span><span className="big">{rules.length}</span></div>
                <div className="m" style={{ flex: 1, minWidth: 180 }}>
                  <span className="lbl">Combinations remaining</span>
                  <span className="big">{(shaftsLive * headsLive).toLocaleString()}</span>
                </div>
              </div>

              <div className="bench-card">
                <h3>Shaft rules</h3>
                <p className="hint">Click a chip, then say how bad it is. Click a chip again to undo it.</p>
                {dims.filter(d => d.target === 'shaft').map(renderDim)}
              </div>
              <div className="bench-card">
                <h3>Head rules</h3>
                <p className="hint">Loft and brand added per review &mdash; both fully populated.</p>
                {dims.filter(d => d.target === 'head').map(renderDim)}
              </div>

              <div className="bench-nav">
                <button className="bench-btn pri" onClick={() => setPass('prescribe')}>Prescribe &rarr;</button>
                <span className="bench-navnote">
                  {rules.length ? `${rules.length} rule${rules.length === 1 ? '' : 's'} recorded.` : 'State at least one rule, or move on if nothing is disqualifying.'}
                </span>
              </div>
            </>
          ) : pass === 'prescribe' && prescribeUnavailable ? (
            <>
              <div className="bench-head">
                <span className="num">02</span>
                <h1>Prescribe the build</h1>
              </div>
              <div className="bench-card" style={{ borderColor: 'var(--engine)' }}>
                <strong>Prescribe isn't available for this category yet.</strong>
                <p className="hint">
                  Pass 01 (cut) and Pass 04 (build) still work &mdash; skip ahead to those.
                </p>
              </div>
              <div className="bench-nav">
                <button className="bench-btn" onClick={() => setPass('cut')}>&larr; Cut</button>
                <button className="bench-btn pri" onClick={() => setPass('build')}>Build &rarr;</button>
              </div>
            </>
          ) : pass === 'prescribe' ? (
            <>
              <div className="bench-head">
                <span className="num">02</span>
                <h1>Prescribe the build</h1>
                <p>
                  Before you see a single club: what does this golfer actually need? Ranges, not
                  points &mdash; and if he sits between two flexes, take both and say what each has
                  to be.
                </p>
              </div>

              <div className="bench-card">
                <h3>Shaft target</h3>
                <p className="hint">Set the acceptable range and the ideal. Anything left as "no view" is stored as no constraint at all.</p>
                {pxDims.shaft.map(renderPxDim('shaft'))}
              </div>
              <div className="bench-card">
                <h3>Head target</h3>
                {pxDims.head.map(renderPxDim('head'))}
              </div>

              <div className="bench-nav">
                <button className="bench-btn" onClick={() => setPass('cut')}>&larr; Cut</button>
                <button className="bench-btn pri" onClick={() => { setScoreTarget('shaft'); setPass('score'); }}>Score shafts &rarr;</button>
                <span className="bench-navnote">
                  {pxSetCount('shaft') || pxSetCount('head')
                    ? `${pxSetCount('shaft')} shaft + ${pxSetCount('head')} head dimension(s) recorded.`
                    : 'Nothing prescribed yet.'}
                </span>
              </div>
            </>
          ) : pass === 'score' && prescribeUnavailable ? (
            <>
              <div className="bench-head">
                <span className="num">03</span>
                <h1>Score the strip</h1>
              </div>
              <div className="bench-card" style={{ borderColor: 'var(--engine)' }}>
                <strong>The scored strip isn't available for this category yet.</strong>
                <p className="hint">It's drawn from the prescription, which isn't built here.</p>
              </div>
              <div className="bench-nav">
                <button className="bench-btn" onClick={() => setPass('cut')}>&larr; Cut</button>
                <button className="bench-btn pri" onClick={() => setPass('build')}>Build &rarr;</button>
              </div>
            </>
          ) : pass === 'score' ? (
            renderScorePass()
          ) : pass === 'build' ? (
            renderBuildPass()
          ) : (
            renderRevealPass()
          )}
        </main>

        <aside className="bench-side">
          <div className="bench-sbox">
            <h4>Cuts <span style={{ float: 'right' }}>{rules.length}</span></h4>
            {rules.length ? rules.map(r => (
              <div className="bench-srow" key={r.id}>
                <span className="k" title={r.reason || `${r.dimension}`}>{r.reason || r.dimension}</span>
                <span className="v" style={{ color: bandColor(BANDS.findIndex(b => b.lo === r.band_low)) }}>
                  {r.band_low}&ndash;{r.band_high}
                </span>
                <button aria-label={`remove rule: ${r.reason}`} disabled={busy} onClick={() => dropRule(r)}>&times;</button>
              </div>
            )) : <div className="bench-empty">Nothing cut yet.</div>}
          </div>
          <div className="bench-sbox">
            <h4>Live pool</h4>
            <div className="bench-srow"><span className="k">Shafts</span><span className="v">{shaftsLive}/{shafts.length}</span></div>
            <div className="bench-srow"><span className="k">Heads</span><span className="v">{headsLive}/{heads.length}</span></div>
          </div>
          <div className="bench-sbox">
            <h4>Prescribed <span style={{ float: 'right' }}>{prescriptions.length}</span></h4>
            {prescriptions.length ? prescriptions.map(p => (
              <div className="bench-srow" key={p.id}>
                <span className="k">{p.target}.{p.dimension}</span>
                <span className="v">{p.no_view ? 'no view' : (p.levels?.join('/') || `${p.range_low}–${p.range_high}`)}</span>
              </div>
            )) : <div className="bench-empty">Nothing prescribed yet.</div>}
          </div>
          <div className="bench-sbox">
            <h4>Scored <span style={{ float: 'right' }}>{scores.length}</span></h4>
            {scores.length ? scores.map(s => (
              <div className="bench-srow" key={s.id}>
                <span className="k">{s.target}#{s.item_id}</span>
                <span className="v" style={{ color: scoreBandColor(s.score) }}>{s.score}</span>
              </div>
            )) : <div className="bench-empty">Nothing scored yet.</div>}
          </div>
          <div className="bench-sbox">
            <h4>Built <span style={{ float: 'right' }}>{buildScores.length}</span></h4>
            {buildScores.length ? (
              <>
                <div className="bench-srow"><span className="k">Matrix combos</span><span className="v">{matrixRows.length}</span></div>
                <div className="bench-srow"><span className="k">Ideal-fit slots</span><span className="v">{idealRows.length}/10</span></div>
              </>
            ) : <div className="bench-empty">Nothing built yet.</div>}
          </div>
        </aside>
      </div>
    </div>
  );
};

// Interval slider — D2a, ported from the prototype's dual-handle drag slider
// (bench2.template.html's .rng-wrap/.rng-track/.rng-fill/.rng-h/.rng-i/.rng-ghost/.rng-sc,
// its pointerdown/pointermove drag handlers, and the bare-track click that sets
// the ideal marker). Three handles share one track: lo/hi define the acceptable
// range (the fill between them), the diamond is the ideal. The engine's own
// suggestion for this dimension renders as a fixed teal tick (`.bench-rng-ghost`)
// so disagreement between fitter and engine is visible at a glance, per
// §4.2's "the engine ghost is what makes fitter-vs-engine disagreement visible."
const RangeSlider: React.FC<{
  D: PxDimMeta; value?: Prescription; busy: boolean;
  onCommit: (lo: number, hi: number, ideal: number) => void;
}> = ({ D, value, busy, onCommit }) => {
  const [min, max] = D.ext || [0, 0];
  const step = D.unit === '°' ? 0.1 : 1;
  const decimals = step < 1 ? 1 : 0;
  const wrapRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<'lo' | 'hi' | 'ideal' | null>(null);

  const clampRound = (v: number) => {
    const c = Math.max(min, Math.min(max, v));
    return +(Math.round(c / step) * step).toFixed(decimals);
  };
  const initial = () => {
    const lo = value?.range_low ?? min;
    const hi = value?.range_high ?? max;
    const ideal = value?.ideal ?? +(((lo + hi) / 2).toFixed(decimals));
    return { lo, hi, ideal };
  };
  const [draft, setDraft] = useState(initial);
  useEffect(() => {
    setDraft(initial());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.range_low, value?.range_high, value?.ideal]);

  const pct = (v: number) => (max === min ? 0 : ((v - min) / (max - min)) * 100);

  const valueFromClientX = (clientX: number) => {
    const r = wrapRef.current!.getBoundingClientRect();
    return clampRound(min + ((clientX - r.left) / r.width) * (max - min));
  };

  const apply = (which: 'lo' | 'hi' | 'ideal', v: number) => {
    setDraft(d => {
      let { lo, hi, ideal } = d;
      if (which === 'lo') lo = Math.min(v, hi);
      else if (which === 'hi') hi = Math.max(v, lo);
      else ideal = Math.max(lo, Math.min(hi, v));
      const next = { lo, hi, ideal };
      onCommit(next.lo, next.hi, next.ideal);
      return next;
    });
  };

  const onHandlePointerDown = (which: 'lo' | 'hi' | 'ideal') => (e: React.PointerEvent) => {
    if (busy) return;
    draggingRef.current = which;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    apply(which, valueFromClientX(e.clientX));
    e.preventDefault();
    e.stopPropagation();
  };
  const onWrapPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current || busy) return;
    apply(draggingRef.current, valueFromClientX(e.clientX));
  };
  const onWrapPointerUp = () => { draggingRef.current = null; };

  const onTrackClick = (e: React.MouseEvent) => {
    if (busy || draggingRef.current) return;
    apply('ideal', valueFromClientX(e.clientX));
  };

  return (
    <div
      className="bench-rng-wrap" ref={wrapRef}
      onPointerMove={onWrapPointerMove} onPointerUp={onWrapPointerUp} onPointerCancel={onWrapPointerUp}
    >
      <div className="bench-rng-track" onClick={onTrackClick} />
      <div className="bench-rng-fill" style={{ left: `${pct(draft.lo)}%`, width: `${Math.max(0, pct(draft.hi) - pct(draft.lo))}%` }} />
      {D.ghost != null && D.ghost >= min && D.ghost <= max && (
        <div className="bench-rng-ghost" style={{ left: `${pct(D.ghost)}%` }} title={`engine suggests ${D.ghost}${D.unit}`} />
      )}
      <div
        className="bench-rng-h" style={{ left: `${pct(draft.lo)}%` }} role="slider" tabIndex={0}
        aria-label="acceptable low" aria-valuemin={min} aria-valuemax={max} aria-valuenow={draft.lo}
        onPointerDown={onHandlePointerDown('lo')}
      />
      <div
        className="bench-rng-h" style={{ left: `${pct(draft.hi)}%` }} role="slider" tabIndex={0}
        aria-label="acceptable high" aria-valuemin={min} aria-valuemax={max} aria-valuenow={draft.hi}
        onPointerDown={onHandlePointerDown('hi')}
      />
      <div
        className="bench-rng-i" style={{ left: `${pct(draft.ideal)}%` }} role="slider" tabIndex={0}
        aria-label="ideal" aria-valuemin={min} aria-valuemax={max} aria-valuenow={draft.ideal}
        onPointerDown={onHandlePointerDown('ideal')}
      />
      <div className="bench-rng-sc">
        <span>{min}{D.unit}</span>
        <span style={{ color: 'var(--fitter)', fontWeight: 700 }}>
          {draft.lo}&ndash;{draft.hi}{D.unit} &middot; &#9670;{draft.ideal}
        </span>
        <span>{max}{D.unit}</span>
      </div>
    </div>
  );
};

const SubRangeInput: React.FC<{
  ext: [number, number]; value: [number, number]; busy: boolean;
  onCommit: (lo: number, hi: number) => void;
}> = ({ ext, value, busy, onCommit }) => {
  const [valueLo, valueHi] = value;
  const [draft, setDraft] = useState({ lo: valueLo, hi: valueHi });
  useEffect(() => { setDraft({ lo: valueLo, hi: valueHi }); }, [valueLo, valueHi]);
  const commit = () => onCommit(Math.min(draft.lo, draft.hi), Math.max(draft.lo, draft.hi));
  return (
    <span className="bench-subrange">
      <input type="number" min={ext[0]} max={ext[1]} disabled={busy} value={draft.lo}
        onChange={e => setDraft(d => ({ ...d, lo: +e.target.value }))} onBlur={commit} />
      &ndash;
      <input type="number" min={ext[0]} max={ext[1]} disabled={busy} value={draft.hi}
        onChange={e => setDraft(d => ({ ...d, hi: +e.target.value }))} onBlur={commit} />
    </span>
  );
};

export default FittersCornerPage;
